import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Replication and disaster recovery (part 39).
 *
 * Every number on this page came from PostgreSQL: lag is pg_current_wal_lsn minus the standby's
 * replay_lsn, the time figures are pg_stat_replication's write/flush/replay lag intervals, and the
 * promotion verdict is the API's replication domain applied to those fields. The page does not
 * invent a heartbeat and does not treat "no check yet" as healthy: a target without a reading is
 * shown as ยังวัดไม่ได้ with the reason.
 *
 * The check button reads the node the API is connected to. On a primary that is
 * pg_stat_replication (one row per walreceiver); on a standby it is pg_stat_wal_receiver. A target
 * whose application_name has no sender is reported as down, not as zero lag.
 *
 * This page is not routed from App.tsx in this part, and the API mount is intentionally not called
 * from server.go. Both are one-line wiring changes for the operator who turns the feature on.
 */

type Health = 'streaming' | 'catchup' | 'lagging' | 'stalled' | 'down' | 'unknown'
type ReadinessVerdict = 'ready' | 'degraded' | 'blocked' | 'unknown'

interface TargetRow {
  id: string
  name_th: string
  role: 'primary' | 'standby'
  region: string
  host: string
  port: number
  database_name: string
  application_name: string
  rpo_seconds: number
  rto_seconds: number
  priority: number
  status: 'active' | 'paused' | 'retired'
  notes: string
  created_at: string
  updated_at: string
  latest_health: Health
  latest_health_label: string
  lag_known: boolean
  lag_bytes: number
  replay_lag_ms: number
  readiness: ReadinessVerdict
  readiness_label: string
  checked_at?: string
  stale: boolean
  estimated_rpo_seconds: number
  rpo_known: boolean
  rpo_ok: boolean
  estimated_rto_seconds: number
  rto_known: boolean
  rto_ok: boolean
  blockers: Finding[]
  warnings: Finding[]
}

interface Finding {
  code: string
  message: string
}

interface CheckRow {
  id: string
  target_id: string
  captured_at: string
  observed_from: 'primary' | 'standby' | 'api'
  health: Health
  health_label_th: string
  primary_lsn: string
  sent_lsn: string
  replay_lsn: string
  received_lsn: string
  lag_known: boolean
  lag_bytes: number
  write_lag_ms: number
  flush_lag_ms: number
  replay_lag_ms: number
  replay_rate_bps: number
  rpo_seconds: number
  rpo_known: boolean
  rpo_ok: boolean
  rto_seconds: number
  rto_known: boolean
  rto_ok: boolean
  readiness: ReadinessVerdict
  blockers: Finding[]
  warnings: Finding[]
  sender_state: string
  sync_state: string
  wal_receiver_status: string
  application_name: string
  client_addr: string
  created_at: string
}

interface ReadinessRow {
  target_id: string
  name_th: string
  role: string
  region: string
  priority: number
  verdict: ReadinessVerdict
  verdict_label: string
  health: Health
  health_label: string
  lag_known: boolean
  lag_bytes: number
  estimated_rpo_seconds: number
  rpo_known: boolean
  rpo_ok: boolean
  estimated_rto_seconds: number
  rto_known: boolean
  rto_ok: boolean
  checked_at?: string
  age_seconds: number
  stale: boolean
  blockers: Finding[]
  warnings: Finding[]
}

interface ReadinessResponse {
  generated_at: string
  targets: ReadinessRow[]
  summary: { total: number; ready: number; degraded: number; blocked: number; unknown: number }
}

const KEYS = {
  targets: ['replication', 'targets'] as const,
  checks: ['replication', 'checks'] as const,
  readiness: ['replication', 'readiness'] as const,
}

export function Replication() {
  const qc = useQueryClient()
  const { me } = useAuth()
  const canManage = me?.role === 'owner' || me?.role === 'admin'

  const [error, setError] = useState('')
  const [name, setName] = useState('')
  const [region, setRegion] = useState('')
  const [host, setHost] = useState('')
  const [port, setPort] = useState('5432')
  const [appName, setAppName] = useState('')
  const [rpoSeconds, setRpoSeconds] = useState('300')
  const [rtoSeconds, setRtoSeconds] = useState('900')

  const targets = useQuery({
    queryKey: KEYS.targets,
    queryFn: () => api<TargetRow[]>('/v1/replication/targets'),
    refetchInterval: 30_000,
  })
  const readiness = useQuery({
    queryKey: KEYS.readiness,
    queryFn: () => api<ReadinessResponse>('/v1/replication/readiness'),
    refetchInterval: 30_000,
  })
  const checks = useQuery({
    queryKey: KEYS.checks,
    queryFn: () => api<CheckRow[]>('/v1/replication/checks?limit=30'),
  })

  const targetNames = useMemo(
    () => new Map((targets.data ?? []).map((t) => [t.id, t.name_th])),
    [targets.data],
  )

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: KEYS.targets })
    void qc.invalidateQueries({ queryKey: KEYS.checks })
    void qc.invalidateQueries({ queryKey: KEYS.readiness })
  }

  const takeCheck = useMutation({
    mutationFn: (targetID?: string) =>
      apiWrite<CheckRow[]>('/v1/replication/checks', 'POST', targetID ? { target_id: targetID } : {}),
    onSuccess: () => {
      setError('')
      refresh()
    },
    onError: (err) => setError(errorText(err, 'ตรวจสถานะการจำลองข้อมูลไม่สำเร็จ')),
  })

  const createTarget = useMutation({
    mutationFn: () =>
      apiWrite<TargetRow>('/v1/replication/targets', 'POST', {
        name_th: name.trim(),
        role: 'standby',
        region: region.trim(),
        host: host.trim(),
        port: Number(port) || 5432,
        application_name: appName.trim(),
        rpo_seconds: rpoSeconds.trim() === '' ? 300 : Number.isFinite(Number(rpoSeconds)) ? Number(rpoSeconds) : 300,
        rto_seconds: Number(rtoSeconds) || 900,
      }),
    onSuccess: () => {
      setError('')
      setName('')
      setRegion('')
      setHost('')
      setAppName('')
      refresh()
    },
    onError: (err) => setError(errorText(err, 'เพิ่มเป้าหมายไม่สำเร็จ')),
  })

  const rows = targets.data ?? []
  const report = readiness.data
  const summary = report?.summary
  const streamingCount = rows.filter((t) => t.latest_health === 'streaming').length
  const needsAttention = rows.filter((t) => t.readiness === 'blocked').length

  const submitTarget = (event: FormEvent) => {
    event.preventDefault()
    setError('')
    createTarget.mutate()
  }

  return (
    <>
      <PageHead
        title="การจำลองข้อมูลและการกู้คืนภัยพิบัติ"
        sub="ตรวจเครื่องสำรองจาก pg_stat_replication และ pg_stat_wal_receiver จริง ไม่ใช่สัญญาณ heartbeat ที่ระบบสร้างขึ้นเอง"
        actions={
          <div className="row" style={{ gap: 8 }}>
            <button
              className="btn btn-sm"
              onClick={() => takeCheck.mutate(undefined)}
              disabled={takeCheck.isPending || rows.length === 0}
            >
              {takeCheck.isPending ? 'กำลังตรวจ…' : 'ตรวจสถานะทั้งหมด'}
            </button>
          </div>
        }
      />

      {error ? (
        <div className="card card-pad" style={{ borderColor: 'var(--color-critical)' }}>
          <span className="sev sev-critical">
            <i className="sev-dot" />
            {error}
          </span>
        </div>
      ) : null}

      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat label="เป้าหมายทั้งหมด" value={number(summary?.total ?? rows.length)} unit="เป้าหมาย" foot="เรียงตามลำดับการเลื่อนขั้น" />
        <Stat
          label="พร้อมเลื่อนขั้น"
          value={number(summary?.ready ?? rows.filter((t) => t.readiness === 'ready').length)}
          unit="เป้าหมาย"
          foot="ผลตรวจสดและอยู่ในงบ RPO/RTO"
        />
        <Stat
          label="เลื่อนขั้นไม่ได้"
          value={number(summary?.blocked ?? needsAttention)}
          unit="เป้าหมาย"
          foot={needsAttention > 0 ? 'ต้องแก้สาเหตุก่อนตัดสินใจ' : 'ไม่มีเป้าหมายที่ถูกบล็อก'}
        />
        <Stat label="สตรีมปกติ" value={number(streamingCount)} unit="เป้าหมาย" foot="ผู้รับ WAL เชื่อมต่อและตามทัน" />
      </div>

      <div className="stack" style={{ gap: 16 }}>
        <div className="card">
          <div className="card-pad" style={{ paddingBottom: 8 }}>
            <div className="row">
              <span className="card-label">เป้าหมายระดับภูมิภาค</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                งบ RPO และ RTO ต่อเป้าหมายเป็นตัวตัดสินความพร้อม
              </span>
            </div>
          </div>
          {targets.isLoading ? (
            <div className="card-pad stack" style={{ gap: 8 }}>
              <Skel h={14} />
              <Skel h={14} w="70%" />
            </div>
          ) : rows.length === 0 ? (
            <div className="card-pad">
              <Empty
                title="ยังไม่มีเป้าหมายการจำลองข้อมูล"
                body="เพิ่มเครื่องสำรองพร้อม application_name ที่ตรงกับ primary_conninfo ของเครื่องนั้น แล้วกดตรวจสถานะเพื่ออ่านค่าจริงจาก PostgreSQL"
              />
            </div>
          ) : (
            <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>เป้าหมาย</th>
                    <th>ภูมิภาค</th>
                    <th>สุขภาพ</th>
                    <th>ข้อมูลค้าง</th>
                    <th>RPO ประมาณ</th>
                    <th>RTO ประมาณ</th>
                    <th>ความพร้อม</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((target) => (
                    <tr key={target.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{target.name_th}</div>
                        <div className="mono dim">
                          {target.application_name} · {target.host}:{target.port}
                        </div>
                        {target.checked_at ? (
                          <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                            ตรวจล่าสุด {thaiDateTime(target.checked_at)}
                            {target.stale ? ' · เก่าเกิน 5 นาที' : ''}
                          </div>
                        ) : (
                          <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                            ยังไม่เคยตรวจ
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="chip">{target.region}</span>
                        <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                          {target.role === 'standby' ? 'เครื่องสำรอง' : 'เครื่องหลัก'}
                        </div>
                      </td>
                      <td>
                        <span className={sevClass(healthTone(target.latest_health))}>
                          <i className="sev-dot" />
                          {target.latest_health_label}
                        </span>
                        {target.blockers.length > 0 ? (
                          <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                            {target.blockers[0].message}
                          </div>
                        ) : null}
                      </td>
                      <td className="tnum">{target.lag_known ? bytesText(target.lag_bytes) : 'ยังวัดไม่ได้'}</td>
                      <td className="tnum">
                        {target.rpo_known ? `${number(target.estimated_rpo_seconds)} วินาที` : 'ยังวัดไม่ได้'}
                        <div className="dim" style={{ fontSize: 11.5 }}>
                          งบ {number(target.rpo_seconds)} วินาที
                        </div>
                      </td>
                      <td className="tnum">
                        {target.rto_known ? `${number(target.estimated_rto_seconds)} วินาที` : 'ยังวัดไม่ได้'}
                        <div className="dim" style={{ fontSize: 11.5 }}>
                          งบ {number(target.rto_seconds)} วินาที
                        </div>
                      </td>
                      <td>
                        <span className={sevClass(verdictTone(target.readiness))}>
                          <i className="sev-dot" />
                          {target.readiness_label}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => takeCheck.mutate(target.id)}
                          disabled={takeCheck.isPending}
                        >
                          ตรวจ
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid-main">
          <div className="stack" style={{ gap: 16 }}>
            <div className="card">
              <div className="card-pad" style={{ paddingBottom: 8 }}>
                <span className="card-label">ผลตรวจล่าสุด</span>
              </div>
              {checks.isLoading ? (
                <div className="card-pad stack" style={{ gap: 8 }}>
                  <Skel h={14} />
                  <Skel h={14} w="60%" />
                </div>
              ) : (checks.data ?? []).length === 0 ? (
                <div className="card-pad">
                  <Empty
                    title="ยังไม่มีผลตรวจ"
                    body="กดตรวจสถานะเพื่ออ่านตำแหน่ง WAL ของเครื่องหลักและเครื่องสำรอง ระบบจะบันทึกไบต์ที่ค้างและประมาณการ RPO/RTO ไว้เป็นหลักฐาน"
                  />
                </div>
              ) : (
                <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>เวลา</th>
                        <th>เป้าหมาย</th>
                        <th>สุขภาพ</th>
                        <th>ไบต์ค้าง</th>
                        <th>ที่มา</th>
                        <th>ความพร้อม</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(checks.data ?? []).map((check) => (
                        <tr key={check.id}>
                          <td className="dim">{thaiDateTime(check.captured_at)}</td>
                          <td>{targetNames.get(check.target_id) ?? 'ไม่พบเป้าหมาย'}</td>
                          <td>
                            <span className={sevClass(healthTone(check.health))}>
                              <i className="sev-dot" />
                              {check.health_label_th}
                            </span>
                          </td>
                          <td className="tnum">{check.lag_known ? bytesText(check.lag_bytes) : 'ยังวัดไม่ได้'}</td>
                          <td className="dim">
                            {check.observed_from === 'primary'
                              ? 'เครื่องหลัก'
                              : check.observed_from === 'standby'
                                ? 'เครื่องสำรอง'
                                : 'เซิร์ฟเวอร์ API'}
                          </td>
                          <td>
                            <span className={sevClass(verdictTone(check.readiness))}>
                              <i className="sev-dot" />
                              {verdictLabel(check.readiness)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {report && report.targets.length > 0 ? (
              <div className="card card-pad">
                <div className="row" style={{ marginBottom: 8 }}>
                  <span className="card-label">รายละเอียดความพร้อม</span>
                  <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                    สร้างเมื่อ {thaiDateTime(report.generated_at)}
                  </span>
                </div>
                <div className="stack" style={{ gap: 14 }}>
                  {report.targets.map((row) => (
                    <div key={row.target_id} className="stack" style={{ gap: 6 }}>
                      <div className="row" style={{ gap: 8 }}>
                        <span style={{ fontWeight: 500 }}>{row.name_th}</span>
                        <span className={sevClass(verdictTone(row.verdict))}>
                          <i className="sev-dot" />
                          {row.verdict_label}
                        </span>
                        <span className="dim mono" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                          {row.health_label} · {row.lag_known ? bytesText(row.lag_bytes) : 'ยังวัดไม่ได้'}
                        </span>
                      </div>
                      {row.blockers.map((finding) => (
                        <div key={finding.code} className="dim" style={{ fontSize: 12 }}>
                          <span className="mono">{finding.code}</span> · {finding.message}
                        </div>
                      ))}
                      {row.warnings.map((finding) => (
                        <div key={finding.code} className="dim" style={{ fontSize: 12 }}>
                          <span className="mono">{finding.code}</span> · {finding.message}
                        </div>
                      ))}
                      {row.blockers.length === 0 && row.warnings.length === 0 ? (
                        <div className="dim" style={{ fontSize: 12 }}>
                          ไม่มีข้อขัดขวาง ผลตรวจสดและอยู่ในงบทั้งหมด
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="stack" style={{ gap: 16 }}>
            {canManage ? (
              <form className="card card-pad" onSubmit={submitTarget}>
                <div className="row" style={{ marginBottom: 4 }}>
                  <span className="card-label">เพิ่มเป้าหมายเครื่องสำรอง</span>
                  <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                    เฉพาะเจ้าของและผู้ดูแลระบบ
                  </span>
                </div>
                <label className="stack" style={{ gap: 6 }}>
                  <span className="dim" style={{ fontSize: 11.5 }}>ชื่อเป้าหมาย</span>
                  <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="ศูนย์สำรอง กรุงเทพฯ" required />
                </label>
                <label className="stack" style={{ gap: 6 }}>
                  <span className="dim" style={{ fontSize: 11.5 }}>ภูมิภาค</span>
                  <input className="input mono" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="ap-southeast-7" required />
                </label>
                <label className="stack" style={{ gap: 6 }}>
                  <span className="dim" style={{ fontSize: 11.5 }}>โฮสต์</span>
                  <input className="input mono" value={host} onChange={(e) => setHost(e.target.value)} placeholder="10.20.0.11" required />
                </label>
                <div className="grid-2" style={{ gap: 12 }}>
                  <label className="stack" style={{ gap: 6 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>พอร์ต</span>
                    <input className="input mono" value={port} onChange={(e) => setPort(e.target.value)} inputMode="numeric" />
                  </label>
                  <label className="stack" style={{ gap: 6 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>application_name</span>
                    <input className="input mono" value={appName} onChange={(e) => setAppName(e.target.value)} placeholder="auditai-dr-bkk" required />
                  </label>
                </div>
                <div className="grid-2" style={{ gap: 12 }}>
                  <label className="stack" style={{ gap: 6 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>งบ RPO (วินาที)</span>
                    <input className="input mono" value={rpoSeconds} onChange={(e) => setRpoSeconds(e.target.value)} inputMode="numeric" />
                  </label>
                  <label className="stack" style={{ gap: 6 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>งบ RTO (วินาที)</span>
                    <input className="input mono" value={rtoSeconds} onChange={(e) => setRtoSeconds(e.target.value)} inputMode="numeric" />
                  </label>
                </div>
                <button className="btn btn-primary" type="submit" disabled={createTarget.isPending}>
                  {createTarget.isPending ? 'กำลังบันทึก…' : 'เพิ่มเป้าหมาย'}
                </button>
              </form>
            ) : null}

            <div className="card card-pad">
              <span className="card-label">กติกาที่ใช้ตัดสิน</span>
              <div className="stack" style={{ gap: 8, marginTop: 8, fontSize: 12 }}>
                <div className="dim">ไบต์ค้าง = pg_current_wal_lsn − replay_lsn ของผู้รับ WAL</div>
                <div className="dim">RPO มาจาก replay_lag เมื่อมีค่า มิฉะนั้นใช้ไบต์ค้างหารความเร็วเล่น WAL</div>
                <div className="dim">RTO = เวลาเลื่อนขั้น 30 วินาที + เวลาตามข้อมูลค้างให้ทัน</div>
                <div className="dim">ผลตรวจที่เก่ากว่า 5 นาทีใช้ตัดสินการเลื่อนขั้นไม่ได้</div>
                <div className="dim">ผู้รับ WAL หยุดหรือไม่มีผู้ส่ง = ขาดการเชื่อมต่อ ไม่ใช่ค่าล่าช้าเป็นศูนย์</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function sevClass(tone: string): string {
  return tone === 'info' ? 'sev dim' : `sev sev-${tone}`
}

function healthTone(health: Health): string {
  switch (health) {
    case 'streaming':
      return 'low'
    case 'catchup':
      return 'medium'
    case 'lagging':
    case 'stalled':
      return 'high'
    case 'down':
      return 'critical'
    default:
      return 'info'
  }
}

function verdictTone(verdict: ReadinessVerdict): string {
  switch (verdict) {
    case 'ready':
      return 'low'
    case 'degraded':
      return 'medium'
    case 'blocked':
      return 'critical'
    default:
      return 'info'
  }
}

function verdictLabel(verdict: ReadinessVerdict): string {
  switch (verdict) {
    case 'ready':
      return 'พร้อมเลื่อนขั้น'
    case 'degraded':
      return 'มีเงื่อนไข'
    case 'blocked':
      return 'ยังไม่ได้'
    default:
      return 'ยังสรุปไม่ได้'
  }
}

function bytesText(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 ไบต์'
  if (bytes < 1024) return `${number(bytes)} ไบต์`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}
